import { plainText } from '../rendering/textLayout';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { TextParagraph } from '../model';
import {
  alignRange,
  emptyParagraphs,
  formatAt,
  formatRange,
  rangeFormat,
  replaceRange,
  textLength,
  type Alignment,
  type FormatPatch,
  type RangeFormat,
  type RunFormat
} from '../domain/richText';
import { rangeToOffsets, readSelection, renderParagraphsInto, writeSelection } from './richTextDom';
import { useTypingBursts } from './useTypingBursts';
import { isPrimaryModifier } from './escapeArbiter';
import { symbolShortcut } from '../domain/symbolShortcuts';

// What the host's formatting toolbar can read and do. Commands apply to the current selection;
// with a collapsed caret, character formatting applies to the next typed text instead (#609 T1).
export interface TextToolbarControls {
  readonly format: RangeFormat;
  readonly toggleBold: () => void;
  readonly toggleItalic: () => void;
  readonly toggleScript: (script: 'sub' | 'super') => void;
  readonly setAlign: (align: Alignment) => void;
  readonly setFontFamily: (family: string) => void;
  readonly setFontSize: (points: number) => void;
  /** #1137 follow-up -- types `text` at the caret (over the selection), as if typed. */
  readonly insertText: (text: string) => void;
}

export interface RichTextEditorProps {
  // The annotation's paragraphs as the document holds them; null while not (yet) in the document.
  readonly committed: readonly TextParagraph[] | null;
  readonly left: number;
  readonly top: number;
  readonly zoom: number;
  // The paper's width: the formatting bar is kept inside it.
  readonly boundsWidth: number;
  readonly color?: string;
  readonly centeredWidth?: number;
  readonly centeredHeight?: number;
  readonly initialInput?: string;
  readonly label: string;
  readonly onCommit: (paragraphs: readonly TextParagraph[]) => void;
  // `closingPointerTimeStamp` is set when a press outside the editor ended it, so the host can
  // ignore that same press (React may already have re-rendered before the host's handler runs).
  readonly onExit: (closingPointerTimeStamp?: number) => void;
  readonly onUndo?: () => void;
  readonly onRedo?: () => void;
  readonly renderToolbar?: (controls: TextToolbarControls) => ReactNode;
}

// The host toolbar renders as its own component: its controls are event handlers, never called
// while rendering.
function ToolbarSlot({
  render,
  controls
}: {
  readonly render: (controls: TextToolbarControls) => ReactNode;
  readonly controls: TextToolbarControls;
}) {
  return render(controls);
}

const same = (a: readonly TextParagraph[], b: readonly TextParagraph[]) => JSON.stringify(a) === JSON.stringify(b);

// #756 -- inline editing of one standalone text annotation. A contentEditable overlay gives native
// caret, selection, IME and mobile keyboards; every edit is intercepted in `beforeinput`, applied to
// the run model (domain/richText) and the DOM rebuilt from it, so the model is the only truth.
// Composition is the exception: the browser owns the DOM until `compositionend`, then the composed
// string is applied in one edit, so a composition is never split.
export function RichTextEditor({
  committed,
  left,
  top,
  zoom,
  boundsWidth,
  color,
  centeredWidth,
  centeredHeight,
  initialInput,
  label,
  onCommit,
  onExit,
  onUndo,
  onRedo,
  renderToolbar
}: RichTextEditorProps) {
  const initial = committed ?? emptyParagraphs();
  const expectedRef = useRef<readonly TextParagraph[]>(initial);
  // #762 acceptance ("text Undo cannot mutate chemistry"): Undo/Redo inside the editor step only
  // through the history entries this editing session made. A commit that changes nothing makes
  // no entry in the document, so it isn't counted.
  const undoableRef = useRef(0);
  const redoableRef = useRef(0);
  const bursts = useTypingBursts(initial, (paragraphs) => {
    if (!same(expectedRef.current, paragraphs)) {
      undoableRef.current += 1;
      redoableRef.current = 0;
    }
    expectedRef.current = paragraphs;
    onCommit(paragraphs);
  });
  const undoText = () => {
    bursts.flush();
    if (undoableRef.current === 0) return;
    undoableRef.current -= 1;
    redoableRef.current += 1;
    onUndo?.();
  };
  const redoText = () => {
    bursts.flush();
    if (redoableRef.current === 0) return;
    redoableRef.current -= 1;
    undoableRef.current += 1;
    onRedo?.();
  };
  // The text as last shown; edits update it synchronously (bursts.draft lags a render behind).
  const draftRef = useRef(initial);
  const rootRef = useRef<HTMLDivElement>(null);
  const chromeRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Horizontal shift that keeps the bar inside the paper (8px gutters).
  const [toolbarShift, setToolbarShift] = useState(0);
  useLayoutEffect(() => {
    const width = toolbarRef.current?.offsetWidth ?? 0;
    const maxLeft = boundsWidth - 8 - width;
    // eslint-disable-next-line react/set-state-in-effect -- measured layout, applied before paint
    setToolbarShift(Math.max(8 - left, Math.min(0, maxLeft - left)));
  }, [boundsWidth, left]);
  // The selection as the editor last placed or observed it; a different one means the user moved
  // the caret, which ends the burst.
  const selectionRef = useRef({ start: textLength(initial), end: textLength(initial) });
  const typingFormatRef = useRef<RunFormat | null>(null);
  const composingRef = useRef<{ start: number; end: number; base: readonly TextParagraph[] } | null>(null);
  const [format, setFormat] = useState<RangeFormat>(() =>
    rangeFormat(initial, textLength(initial), textLength(initial))
  );

  const refreshFormat = useCallback(() => {
    const { start, end } = selectionRef.current;
    const base = rangeFormat(draftRef.current, start, end);
    const typing = typingFormatRef.current;
    setFormat(
      typing && start === end
        ? {
            ...base,
            bold: typing.bold === true,
            italic: typing.italic === true,
            script: typing.script,
            fontFamily: typing.fontFamily,
            fontSize: typing.fontSize
          }
        : base
    );
  }, []);

  const show = useCallback(
    (paragraphs: readonly TextParagraph[], start: number, end: number = start) => {
      const root = rootRef.current;
      if (!root) return;
      draftRef.current = paragraphs;
      renderParagraphsInto(root, paragraphs, zoom);
      selectionRef.current = { start, end };
      if (root.ownerDocument.activeElement === root) writeSelection(root, paragraphs, start, end);
      refreshFormat();
    },
    [refreshFormat, zoom]
  );

  // First paint: content, focus, caret at the end.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    renderParagraphsInto(root, draftRef.current, zoom);
    root.focus({ preventScroll: true });
    writeSelection(root, draftRef.current, selectionRef.current.start, selectionRef.current.end);
    // Zoom changes re-render at the new size, keeping the selection.
  }, [zoom]);

  // Undo/redo (or any outside change) replaced the committed text: follow it; if the annotation
  // was removed, editing ends.
  useEffect(() => {
    const current = committed ?? emptyParagraphs();
    if (same(current, expectedRef.current)) return;
    if (committed === null && !same(expectedRef.current, emptyParagraphs())) {
      onExit();
      return;
    }
    expectedRef.current = current;
    bursts.reset(current);
    const length = textLength(current);
    show(current, Math.min(selectionRef.current.start, length), Math.min(selectionRef.current.end, length));
  }, [bursts, committed, onExit, show]);

  const finish = useCallback(
    (closingPointerTimeStamp?: number) => {
      bursts.flush();
      onExit(closingPointerTimeStamp);
    },
    [bursts, onExit]
  );

  // Clicking anywhere outside the editor and its toolbar commits and exits (#609 T2).
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && chromeRef.current?.contains(target)) return;
      finish(event.timeStamp);
    };
    document.addEventListener('pointerdown', outside, true);
    return () => document.removeEventListener('pointerdown', outside, true);
  }, [finish]);

  // selectionchange is queued by the browser. Commands and toolbar focus transfers must
  // capture the live selection synchronously rather than wait for that notification.
  const synchronizeSelection = useCallback(() => {
    const root = rootRef.current;
    if (!root || composingRef.current) return selectionRef.current;
    const next = readSelection(root, draftRef.current);
    if (!next) return selectionRef.current;
    const previous = selectionRef.current;
    if (next.start === previous.start && next.end === previous.end) return previous;
    selectionRef.current = next;
    typingFormatRef.current = null;
    bursts.flush();
    refreshFormat();
    return next;
  }, [bursts, refreshFormat]);

  // Caret movement by the user (arrows, clicks, selection drags) ends the burst.
  useEffect(() => {
    const changed = () => {
      synchronizeSelection();
    };
    document.addEventListener('selectionchange', changed);
    return () => document.removeEventListener('selectionchange', changed);
  }, [synchronizeSelection]);

  const currentRange = () => {
    const root = rootRef.current;
    return (root && readSelection(root, draftRef.current)) ?? selectionRef.current;
  };

  const type = (start: number, end: number, text: string) => {
    const typing = typingFormatRef.current ?? formatAt(draftRef.current, start);
    // #1137 follow-up -- a character that completes a symbol shortcut ("->", "\Delta ") types the symbol.
    const shortcut =
      text.length === 1 && start === end ? symbolShortcut(plainText(draftRef.current).slice(0, start) + text) : null;
    if (shortcut) {
      start -= shortcut.length - 1;
      text = shortcut.symbol;
    }
    const next = replaceRange(draftRef.current, start, end, text, typing);
    // Once typed, the format lives in the text itself and the caret picks it up from there.
    if (text) typingFormatRef.current = null;
    bursts.edit(next);
    show(next, start + text.length);
  };

  const seeded = useRef(false);
  useEffect(() => {
    if (!initialInput || seeded.current) return;
    seeded.current = true;
    const end = plainText(draftRef.current).length;
    type(end, end, initialInput);
  }, [initialInput]);

  const formatSelection = (patch: FormatPatch) => {
    const { start, end } = synchronizeSelection();
    if (start === end) {
      // Collapsed caret: the next typed text takes the format; nothing changes yet.
      const base = typingFormatRef.current ?? formatAt(draftRef.current, start);
      const merged: Record<string, unknown> = { ...base };
      for (const [key, value] of Object.entries(patch)) {
        if (value === null) delete merged[key];
        else if (value !== undefined) merged[key] = value;
      }
      typingFormatRef.current = merged as RunFormat;
      refreshFormat();
    } else {
      const next = formatRange(draftRef.current, start, end, patch);
      bursts.commitStep(next);
      show(next, start, end);
    }
    rootRef.current?.focus({ preventScroll: true });
    if (rootRef.current) writeSelection(rootRef.current, draftRef.current, start, end);
  };

  const currentFormat = () => {
    const { start, end } = synchronizeSelection();
    return start === end && typingFormatRef.current
      ? typingFormatRef.current
      : rangeFormat(draftRef.current, start, end);
  };

  const controls: TextToolbarControls = {
    format,
    toggleBold: () => formatSelection({ bold: currentFormat().bold === true ? null : true }),
    toggleItalic: () => formatSelection({ italic: currentFormat().italic === true ? null : true }),
    toggleScript: (script) => formatSelection({ script: currentFormat().script === script ? null : script }),
    setAlign: (align) => {
      const { start, end } = synchronizeSelection();
      const next = alignRange(draftRef.current, start, end, align);
      bursts.commitStep(next);
      show(next, start, end);
      rootRef.current?.focus({ preventScroll: true });
      if (rootRef.current) writeSelection(rootRef.current, next, start, end);
    },
    setFontFamily: (fontFamily) => formatSelection({ fontFamily }),
    setFontSize: (fontSize) => {
      if (Number.isFinite(fontSize) && fontSize > 0) formatSelection({ fontSize });
    },
    insertText: (text) => {
      const { start, end } = synchronizeSelection();
      rootRef.current?.focus({ preventScroll: true });
      type(start, end, text);
    }
  };

  // Every edit except composition goes through the model. React's onBeforeInput is a keypress/
  // textInput polyfill without `inputType`, so the native event is subscribed directly.
  const handleBeforeInput = (event: InputEvent) => {
    if (event.isComposing || event.inputType === 'insertCompositionText') return;
    event.preventDefault();
    const root = rootRef.current;
    if (!root) return;
    const target = event.getTargetRanges?.()[0];
    const range = (target && rangeToOffsets(root, draftRef.current, target)) ?? currentRange();
    let { start, end } = range;
    switch (event.inputType) {
      case 'insertText':
      case 'insertReplacementText':
        type(start, end, event.data ?? event.dataTransfer?.getData('text/plain') ?? '');
        return;
      case 'insertParagraph':
      case 'insertLineBreak':
        type(start, end, '\n');
        return;
      case 'insertFromPaste':
      case 'insertFromDrop':
      case 'insertFromYank':
        type(start, end, event.dataTransfer?.getData('text/plain') ?? event.data ?? '');
        return;
      case 'historyUndo':
        undoText();
        return;
      case 'historyRedo':
        redoText();
        return;
      case 'formatBold':
        controls.toggleBold();
        return;
      case 'formatItalic':
        controls.toggleItalic();
        return;
      case 'formatSubscript':
        controls.toggleScript('sub');
        return;
      case 'formatSuperscript':
        controls.toggleScript('super');
        return;
      default:
        if (event.inputType.startsWith('delete')) {
          if (start === end && !target) {
            // No target range from the browser: delete one character.
            if (event.inputType.endsWith('Backward')) start = Math.max(0, start - 1);
            else end = Math.min(textLength(draftRef.current), end + 1);
          }
          if (start !== end) type(start, end, '');
        }
    }
  };
  const beforeInputRef = useRef(handleBeforeInput);
  useEffect(() => {
    beforeInputRef.current = handleBeforeInput;
  });
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const listener = (event: Event) => beforeInputRef.current(event as InputEvent);
    root.addEventListener('beforeinput', listener);
    return () => root.removeEventListener('beforeinput', listener);
  }, []);

  return (
    <div
      ref={chromeRef}
      className={`text-annotation-editor${centeredWidth ? ' callout-text-editor' : ''}`}
      style={{
        left,
        top,
        ...(centeredWidth
          ? {
              width: centeredWidth,
              height: centeredHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }
          : {})
      }}
      onPointerDownCapture={() => {
        synchronizeSelection();
      }}
      // The paper must not start a gesture under the editor.
      onPointerDown={(event) => event.stopPropagation()}
    >
      {renderToolbar ? (
        // Near the paper's top edge the bar flips below the text so it is not clipped.
        <div
          ref={toolbarRef}
          className={`text-annotation-toolbar${top < 56 ? ' below' : ''}`}
          style={{ left: toolbarShift, maxWidth: Math.max(160, boundsWidth - 16) }}
        >
          <ToolbarSlot render={renderToolbar} controls={controls} />
        </div>
      ) : null}
      <div
        ref={rootRef}
        className="text-annotation-input"
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        contentEditable
        suppressContentEditableWarning
        tabIndex={0}
        spellCheck={false}
        onBlur={() => {
          synchronizeSelection();
        }}
        style={{
          color: color ?? 'var(--text)',
          lineHeight: 1.25,
          ...(centeredWidth
            ? { width: Math.max(8, centeredWidth - 28 * zoom), whiteSpace: 'pre-wrap', textAlign: 'center' }
            : {})
        }}
        onKeyDown={(event) => {
          // Keys the editor consumes never reach the canvas shortcuts (#609 T2, #612).
          event.stopPropagation();
          const mod = isPrimaryModifier(event);
          if (event.key === 'Escape') {
            event.preventDefault();
            bursts.cancel();
            onExit();
          } else if (mod && event.key.toLowerCase() === 'z') {
            event.preventDefault();
            if (event.shiftKey) redoText();
            else undoText();
          } else if (mod && event.key.toLowerCase() === 'y') {
            event.preventDefault();
            redoText();
          } else if (mod && event.key.toLowerCase() === 'b') {
            event.preventDefault();
            controls.toggleBold();
          } else if (mod && event.key.toLowerCase() === 'i') {
            event.preventDefault();
            controls.toggleItalic();
          }
        }}
        onCompositionStart={() => {
          const { start, end } = currentRange();
          composingRef.current = { start, end, base: draftRef.current };
          bursts.hold(true);
        }}
        onCompositionEnd={(event) => {
          const composition = composingRef.current;
          composingRef.current = null;
          bursts.hold(false);
          if (!composition) return;
          draftRef.current = composition.base;
          type(composition.start, composition.end, event.data);
        }}
      />
    </div>
  );
}
